import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Swords, Lock, HelpCircle, ScrollText, Globe, Scale, Zap, Music, Shield, X, Check } from 'lucide-react';
import CauriIcon from './common/CauriIcon';
import { CategoryDictionary, WordData, UserProgress, LearnedWord } from '../types/types';
import { getLanguageData } from '../data/languages';
import { useTheme } from './ThemeContext';
import CategoryPreview from './CategoryPreview';
import KamaCategoryPreview from './kama/KamaCategoryPreview';
import './CategorySelection.css';
import FloatingBackButton from './FloatingBackButton';
import TribalBorder from './TribalBorder';
import { CollectibleDef, getCollectibles, getUnlockedIds } from '../utils/collectibles';
import CollectibleIcon from './CollectibleIcon';
import IbisTrailWalker from './IbisTrailWalker';
import { readShields, SESSION_DATE, getStreakChain } from '../hooks/useDailyStreak';
import supabaseProgressService from '../services/supabaseProgressService';
import { QuizTheme } from '../data/miniGamesData';
import { SONGS_BY_LANGUAGE } from '../data/songData';
import { CONVERSATIONS_BY_LANGUAGE } from '../data/conversationsData';
import { KAMA_NON_VOCAB_CATEGORIES, KAMA_NON_VOCAB_KEYS_IN_LECTURE } from '../data/languages/kama/nonVocab';
import { displayCategoryLabel } from '../utils/categoryLabels';
import { LESSON_BREAKS, grammarSlots, vocabSplit } from '../utils/trailSplit';
import { EmojiText } from './common/Picto';
// ─── Types ────────────────────────────────────────────────────────────────────

type MiniGameType = 'culture' | 'challenge' | 'music';

const QUIZ_THEMES_ON_TRAIL: Array<{ theme: QuizTheme; emoji: string; unitId: number; sublabel: string }> = [
  { theme: 'Histoire',   emoji: '📜', unitId: 1, sublabel: 'Héros & empires'    },
  { theme: 'Géographie', emoji: '🌍', unitId: 2, sublabel: 'Terres & fleuves'   },
  { theme: 'Politique',  emoji: '⚖️', unitId: 3, sublabel: 'Pouvoirs & société' },
];
// Icône trait par thème (remplace QUIZ_THEMES_ON_TRAIL[].emoji au rendu — no-emoji design system)
const QUIZ_THEME_ICONS: Partial<Record<QuizTheme, React.ComponentType<{ size?: number | string; strokeWidth?: number | string }>>> = {
  'Histoire': ScrollText,
  'Géographie': Globe,
  'Politique': Scale,
};
const QUIZ_TOTAL = 10;

const readQuizBestScore = (lang: string, theme: QuizTheme): number => {
  try { return parseInt(localStorage.getItem(`${lang}-quiz-best-${theme}`) || '0', 10); }
  catch { return 0; }
};

const readQuizInProgressIndex = (lang: string, theme: QuizTheme): number => {
  try {
    const raw = localStorage.getItem(`${lang}-quiz-inprogress-${theme}`);
    if (!raw) return 0;
    const { currentIndex, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > 24 * 60 * 60 * 1000) return 0;
    return (currentIndex as number) ?? 0;
  } catch { return 0; }
};

interface CategorySelectionProps {
  languageCode: string;
  categories: CategoryDictionary;
  categoryIllustrations: { [key: string]: string };
  onSelectCategory: (category: string, wordRange?: { start: number; end: number }) => void;
  onStartRevision: (category: string) => void;
  completedCategories: string[];
  sessionLearnedWords: Set<string>;
  userProgress: UserProgress;
  onBackToMenu?: () => void;
  isMobileView?: boolean;
  onSelectGrammar?: (unit: number, subcatFilter?: string[]) => void;
  onSelectConstruction?: (sentenceCategories: string[]) => void;
  subscriptionTier?: string;
  onOpenSubscription?: () => void;
  onOpenMiniGame?: (game: MiniGameType) => void;
  onSelectQuizTheme?: (theme: QuizTheme) => void;
  pendingChallengesCount?: number;
  onSelectConversation?: (conversationId: string) => void;
  activationKey?: number;
  // Kama's node-click flow goes straight into KamaCategoryPreview/KamaLearnMode
  // rather than through onSelectCategory + the generic LearnMode App.tsx
  // renders elsewhere, so streak/XP/stats reporting for Kama has to be
  // threaded down into this overlay directly instead.
  onKamaLessonComplete?: (category: string, words: LearnedWord[], isFinalSegment?: boolean) => void;
  onKamaAnswer?: (correct: boolean) => void;
  // Excludes narrative/methodology entries from Kama's post-browse test —
  // App.tsx supplies the real predicate built from its KAMA_NON_VOCAB_*
  // exclusion sets, keyed by category since this screen handles every node.
  isKamaWordTestable?: (category: string, key: string) => boolean;
}

interface TrailLandmark {
  atStepIndex?: number;
  atEndOfUnit?: number;
  emoji: string;
  label: string;
  sublabel: string;
  game: MiniGameType;
}

// Les arènes (Mode défi) ne sont plus sur le sentier : Défier un ami est dans l'onglet Pratiquer.
const SCENE_MAX_SONGS = 2; // max chansons par scène

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const cleanWord = (word: string): string => word.split('(')[0].trim();

// Maps getVocabUnitWord()'s output (derived from the raw French category
// key — see below) to an i18n key. The derivation itself must keep reading
// the untranslated key so it isn't affected by the interface language.
const VOCAB_UNIT_WORD_KEYS: Record<string, string> = {
  mots: 'categorySelection.vocabUnitWords.mots',
  salutations: 'categorySelection.vocabUnitWords.salutations',
  couleurs: 'categorySelection.vocabUnitWords.couleurs',
  verbes: 'categorySelection.vocabUnitWords.verbes',
  nombres: 'categorySelection.vocabUnitWords.nombres',
  animaux: 'categorySelection.vocabUnitWords.animaux',
  objets: 'categorySelection.vocabUnitWords.objets',
  'divinités': 'categorySelection.vocabUnitWords.divinites',
  villes: 'categorySelection.vocabUnitWords.villes',
  pharaons: 'categorySelection.vocabUnitWords.pharaons',
  plantes: 'categorySelection.vocabUnitWords.plantes',
};
const displayVocabUnitWord = (word: string, t: TFunction): string => {
  const key = VOCAB_UNIT_WORD_KEYS[word];
  return key ? t(key) : word;
};

// Maps a UNIT_CONFIGS unit id to its i18n key namespace.
const UNIT_TEXT_KEYS: Record<number, string> = {
  1: 'categorySelection.units.unit1',
  2: 'categorySelection.units.unit2',
  3: 'categorySelection.units.unit3',
  4: 'categorySelection.units.unit4',
};
const displayUnitTitle = (unitId: number, t: TFunction): string => {
  const key = UNIT_TEXT_KEYS[unitId];
  return key ? t(`${key}.title`) : '';
};
const displayUnitSubtitle = (unitId: number, t: TFunction): string => {
  const key = UNIT_TEXT_KEYS[unitId];
  return key ? t(`${key}.subtitle`) : '';
};

// Quiz theme (QuizTheme, e.g. 'Histoire') display label/sublabel — the raw
// theme value is still used everywhere else (id generation, onSelectQuizTheme,
// localStorage keys...).
const displayQuizThemeLabel = (theme: string, t: TFunction): string =>
  t(`categorySelection.quizThemes.${theme}.label`, { defaultValue: theme });
const displayQuizThemeSublabel = (theme: string, t: TFunction): string =>
  t(`categorySelection.quizThemes.${theme}.sublabel`, { defaultValue: '' });

// ─── Unit configuration (shared by all languages) ────────────────────────────
// Categories absent from a language are automatically skipped.

export const UNIT_CONFIGS = [
  {
    id: 1,
    title: 'Premiers pas',
    subtitle: 'Prends pied sur les rives du Nil',
    icon: '𓀀',
    gradient: 'linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-dark) 100%)',
    shadowColor: 'rgba(200,112,64,0.4)',
    categories: [
      'Alphabet copte',             // clé copte uniquement — lire l'écriture avant le vocabulaire
      'Alphabet yoruba',            // clé yoruba uniquement — les lettres avant le vocabulaire
      'Salutations et expressions courantes',
      'Lire les hiéroglyphes',      // clé Kama uniquement — méthode de lecture avant le vocabulaire
      'Phrase',                     // clé Kama uniquement — ordre des mots, juste après la méthode de lecture
      'Phrase — le nom',            // clé Kama uniquement — adjectif et génitifs
      'Phrase — pronoms',           // clé Kama uniquement — pronoms, pluriel, duel
      'Lecture analytique',         // clé Kama uniquement — nœud dédié au principe transversal signe directeur/complément/déterminatif
      'Lecture analytique — déterminatifs', // clé Kama uniquement — le trait, l'homographie, les déterminatifs multiples
      'Lecture analytique — Kemet',      // clé Kama uniquement — la racine km, jusqu'au nom du pays
      'Lecture analytique — histoire',    // clé Kama uniquement — Diodore, Champollion, le copte
      'Famille',
      'Couleurs',
      'Verbes',                    // verbes dès le départ → formation de phrases immédiate
    ],
  },
  {
    id: 2,
    title: 'Vie quotidienne',
    subtitle: 'Explore les marchés et la nature',
    icon: '𓇳',
    gradient: 'linear-gradient(135deg, #D4784A 0%, #8A3E18 100%)',
    shadowColor: 'rgba(200,112,64,0.35)',
    categories: [
      'Nombres',                   // nombres après les bases sociales
      'Animaux',
      'Nourriture',
      'Corps',
    ],
  },
  {
    id: 3,
    title: 'Explorer',
    subtitle: 'Voyage, découvre et agis',
    icon: '𓋴',
    gradient: 'linear-gradient(135deg, #5A3A20 0%, #3A2010 100%)',
    shadowColor: 'rgba(90,58,32,0.5)',
    categories: [
      'Temps',                     // concepts temporels plus abstraits en dernière unité
      'Objets du quotidien',
      'Objets sacrés et du quotidien',   // clé Kama pour la même thématique
    ],
  },
  {
    id: 4,
    title: 'Panthéon et royaume',
    subtitle: 'Dieux, villes et pharaons du Nil',
    icon: '𓇳',
    gradient: 'linear-gradient(135deg, var(--gold-dark) 0%, #3A2010 100%)',
    shadowColor: 'rgba(122,62,24,0.4)',
    categories: [
      // N'existe que côté Kama — invisible pour les autres langues,
      // dont la categoryList ne contient aucune de ces clés.
      'Divinités',
      'Villes',
      'Pharaons',
      'Plantes',
      'Sur les murs des temples',  // clé Kama uniquement — photos de temples et stèles, en fin de parcours
    ],
  },
];

const UNLOCK_THRESHOLD = 0; // % moyen d'une unite pour debloquer la suivante
const GOAL_KEY     = 'jogolinga-daily-goal';
const GOAL_OPTIONS = [
  { xp: 20,  label: 'Léger',    sub: '~5 min' },
  { xp: 50,  label: 'Régulier', sub: '~10 min' },
  { xp: 100, label: 'Intensif', sub: '~20 min' },
] as const;
// Display label/sub for each option come from i18n — xp is the only value
// actually used for logic (localStorage, comparisons).
const GOAL_OPTION_KEYS: Record<number, string> = { 20: 'light', 50: 'regular', 100: 'intensive' };

const getDailyXP = (lang: string): number => {
  try {
    const key      = `${lang}-daily-xp-start`;
    const totalXP  = parseInt(localStorage.getItem(`${lang}-totalXP`) || '0');
    const stored   = localStorage.getItem(key);
    const parsed   = stored ? JSON.parse(stored) : null;
    if (!parsed || parsed.date !== SESSION_DATE) {
      // provisional : l'XP local peut être en retard sur Supabase (XP gagné sur
      // un autre appareil). App.tsx recale la baseline sur l'XP serveur au chargement.
      localStorage.setItem(key, JSON.stringify({ date: SESSION_DATE, xp: totalXP, provisional: true }));
      return 0;
    }
    return Math.max(0, totalXP - parsed.xp);
  } catch { return 0; }
};

const DAY_LABELS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

/**
 * Génère 7 cases ancrées sur les dates réelles. Aujourd'hui est toujours
 * à la position 3. Jours passés : actifs uniquement s'ils font partie de la
 * chaîne consécutive actuelle (sans interruption depuis le jour le plus récent).
 * Aujourd'hui : actif uniquement si l'objectif de CETTE langue est atteint.
 */
const getStreakCalendar = (activityHistory: string[], todayDone = false, currentStreak = -1): { label: string; active: boolean; isToday: boolean; isFuture: boolean }[] => {
  const todayPos = 3;
  const anchor = new Date(SESSION_DATE + 'T12:00:00'); // session date, noon to avoid DST issues

  // Jours de la chaîne de streak actuelle, jours de grâce tolérés (même règle
  // que useDailyStreak). Streak explicitement à 0 (cassé) : aucun jour actif.
  const history = todayDone ? [...activityHistory, SESSION_DATE] : activityHistory;
  const streakDates = new Set<string>(currentStreak === 0 ? [] : getStreakChain(history));
  const toLocal = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  return Array.from({ length: 7 }, (_, i) => {
    const offset = i - todayPos;
    const d = new Date(anchor);
    d.setDate(d.getDate() + offset);
    const dateStr = toLocal(d);
    const active = offset < 0
      ? streakDates.has(dateStr)
      : offset === 0
      ? todayDone
      : false;
    return {
      label:    DAY_LABELS[d.getDay()],
      active,
      isToday:  offset === 0,
      isFuture: offset > 0,
    };
  });
};

// ─── Grammar unit keyword matching (mirrors GrammarMode.tsx) ─────────────────
const GRAMMAR_U1_KW = ['pronom', 'classe', 'structure', 'ìjíṣẹ́', 'personal', 'àkọ́kọ́', 'ìṣọ́ (possessive)'];
const GRAMMAR_U2_KW = ['temps verbaux', 'négation', 'question', 'conjugaison', 'particules verbales', 'àṣeyọrí', 'verb tense', 'ọ̀rọ̀ àṣeyọrí'];
const GRAMMAR_U3_KW = ['préposition', 'preposition', 'postposition', 'adjectif', 'adverbe', 'conjonction', 'marqueurs de focus'];
export const grammarSubMatchesUnit = (sub: string, unit: number): boolean => {
  const s = sub.toLowerCase();
  const u1 = GRAMMAR_U1_KW.some(kw => s.includes(kw.toLowerCase()));
  const u2 = GRAMMAR_U2_KW.some(kw => s.includes(kw.toLowerCase()));
  const u3 = GRAMMAR_U3_KW.some(kw => s.includes(kw.toLowerCase()));
  if (unit === 1) return u1;
  if (unit === 2) return u2;
  if (unit === 3) return u3;
  return !u1 && !u2 && !u3;
};

// Sentence categories that correspond to each unit's vocabulary theme
// Kama : libellé d'étape d'un thème de phrases dont la clé ne dit pas le
// contenu ('Voyages' doit rester la clé exacte, voir SentenceConstructionGame).
const KAMA_SENTENCE_THEME_LABELS: Record<string, string> = {
  'Salutations': 'Premières règles',
  'Voyages': 'Le Nil et le temps',
};

export const UNIT_SENTENCE_CATEGORIES: string[][] = [
  ['Salutations', 'Présentation', 'Famille et relations'],
  ['Vie quotidienne', 'Au marché'],
  ['Voyages', 'Urgences et santé', 'Loisirs et culture'],
  ['Panthéon et royaume'], // Kama uniquement : seule langue avec une unité 4
];

// ─── Collectibles — see ../utils/collectibles.ts ─────────────────────────────
// (types and data imported above)


// ─── Conversation trail placement ─────────────────────────────────────────────

interface ConversationTrailConfig {
  afterCategory: string;
  unitId: number;
  conversationId: string;
  label: string;
  languageCodes: string[];
}

export const CONVERSATION_TRAIL_CONFIGS: ConversationTrailConfig[] = [
  {
    afterCategory: 'Famille',
    unitId: 1,
    conversationId: 'famille-animaux-ba',
    label: 'Conversation',
    languageCodes: ['ba'],
  },
  {
    afterCategory: 'Nourriture',
    unitId: 2,
    conversationId: 'sugu-jiri-ba',
    label: 'Conversation',
    languageCodes: ['ba'],
  },
  {
    afterCategory: 'Objets du quotidien', // compte, francs, téléphone : après Nombres et le quotidien
    unitId: 3,
    conversationId: 'banki-ba',
    label: 'Conversation',
    languageCodes: ['ba'],
  },
  {
    afterCategory: 'Temps',
    unitId: 3,                     // Temps est maintenant en Unité 3
    conversationId: 'taxi-dramane-ba',
    label: 'Conversation',
    languageCodes: ['ba'],
  },
  {
    afterCategory: 'Nombres',      // heures et prix du billet : une fois les nombres vus
    unitId: 2,
    conversationId: 'gare-sikasso-ba',
    label: 'Conversation',
    languageCodes: ['ba'],
  },
];

const readConvDone = (lang: string, convId: string): number => {
  try {
    const raw = localStorage.getItem(`${lang}-conv-${convId}`);
    return raw ? (JSON.parse(raw) as number) : 0;
  } catch { return 0; }
};

// ─── Bogolan icons ────────────────────────────────────────────────────────────
// diamond → vocabulaire, grid → grammaire, chevron → phrases, speech → conversation

type BogolonVariant = 'diamond' | 'grid' | 'chevron' | 'speech';

const BogolonIcon: React.FC<{ variant: BogolonVariant; size?: number }> = ({ variant, size = 20 }) => {
  const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (variant === 'diamond') return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <polygon points="10,1 19,10 10,19 1,10" {...s} />
      <polygon points="10,5 15,10 10,15 5,10" {...s} />
      <circle cx="10" cy="10" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
  if (variant === 'grid') return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <rect x="1.5" y="1.5" width="7" height="7" {...s} />
      <rect x="11.5" y="1.5" width="7" height="7" {...s} />
      <rect x="1.5" y="11.5" width="7" height="7" {...s} />
      <rect x="11.5" y="11.5" width="7" height="7" {...s} />
    </svg>
  );
  if (variant === 'speech') return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <path d="M2 3 h16 a1.5 1.5 0 0 1 1.5 1.5 v7 a1.5 1.5 0 0 1 -1.5 1.5 h-7 l-4 3 v-3 h-5 a1.5 1.5 0 0 1 -1.5 -1.5 v-7 a1.5 1.5 0 0 1 1.5 -1.5 z" {...s} />
      <circle cx="6.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="10" cy="7.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="13.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <polyline points="1,6 5,11 9,6 13,11 17,6" {...s} />
      <polyline points="1,12 5,17 9,12 13,17 17,12" {...s} />
    </svg>
  );
};

// Estime la durée d'une étape depuis son type (id) et son nombre d'éléments.
// vocab     : ~15s/mot  (3 tests × 5s)
// grammar   : ~45s/règle (lecture + exercice)
// phrases   : ~20s/phrase
// conversation : ~30s/étape de dialogue
// Retourne l'unité de comptage adaptée au nom de la catégorie vocab.
// Ex: "Animaux" → "animaux", "Corps" → "mots" (non comptable), etc.
const getVocabUnitWord = (stepId: string): string => {
  // 3e partie de l'alphabet yoruba = les tons (LESSON_BREAKS), pas des lettres
  if (stepId === 'vocab-Alphabet yoruba-part2') return 'fiches';
  const cat = stepId.replace(/^vocab-/, '').replace(/-part\d+$/, '');
  const EXCEPTIONS: Record<string, string> = {
    'Corps': 'mots', 'Temps': 'mots', 'Nourriture': 'mots', 'Famille': 'mots',
    'Alphabet copte': 'lettres',
    'Alphabet yoruba': 'lettres',
  };
  if (EXCEPTIONS[cat]) return EXCEPTIONS[cat];
  const first = cat.split(' ')[0].toLowerCase();
  return /s$|aux$/.test(first) ? first : 'mots';
};

const estimateStepDuration = (stepId: string, total: number): string => {
  if (!total || total === 0) return '';
  let seconds: number;
  if (stepId.startsWith('grammar-'))      seconds = total * 45;
  else if (stepId.startsWith('phrases-')) seconds = total * 20;
  else if (stepId.startsWith('conversation-')) seconds = total * 30;
  else                                    seconds = total * 15; // vocab
  const minutes = Math.max(1, Math.round(seconds / 60));
  return `~${minutes} min`;
};

const getBogolonVariant = (stepId: string): BogolonVariant => {
  if (stepId.startsWith('grammar-')) return 'grid';
  if (stepId.startsWith('phrases-')) return 'chevron';
  if (stepId.startsWith('conversation-')) return 'speech';
  return 'diamond';
};

// ─── Unit Celebration ─────────────────────────────────────────────────────────

const CONFETTI_ITEMS = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  variant: (['diamond', 'grid', 'chevron'] as BogolonVariant[])[i % 3],
  angle:   (i / 18) * 360 + 7,
  dist:    80 + (i % 4) * 28,
  delay:   0.3 + i * 0.045,
  size:    9 + (i % 4) * 5,
  color:   ['var(--color-accent)', '#D4784A', '#C8A050', 'var(--gold-dark)'][i % 4],
}));

// ─── Daily Goal Modal ─────────────────────────────────────────────────────────

const DailyGoalModal: React.FC<{
  dailyXP: number;
  goal: number;
  streak: number;
  shields: number;
  onClose: () => void;
  onGoalChange: (xp: number) => void;
}> = ({ dailyXP, goal, streak, shields, onClose, onGoalChange }) => {
  const { t } = useTranslation();
  const done = dailyXP >= goal;
  const pct  = Math.min(100, (dailyXP / goal) * 100);
  const actHist: string[] = JSON.parse(localStorage.getItem('jogolinga-activity-history') || '[]');
  const cal  = getStreakCalendar(actHist, done, streak);

  return (
    <motion.div
      className="dgoal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="dgoal-card"
        initial={{ scale: 0.85, opacity: 0, y: 32 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 22 }}
        onClick={e => e.stopPropagation()}
      >
        <button className="dgoal-card__close" onClick={onClose} aria-label={t('categorySelection.close')}><X size={16} strokeWidth={2} /></button>

        <div className="dgoal-card__icon"><Zap size={28} strokeWidth={1.5} /></div>
        <h2 className="dgoal-card__title">{t('categorySelection.dailyGoalTitle')}</h2>

        <div className="dgoal-card__xp-row">
          <span className={`dgoal-card__xp-value${done ? ' dgoal-card__xp-value--done' : ''}`}>
            {Math.min(dailyXP, goal)}
          </span>
          <span className="dgoal-card__xp-sep">/</span>
          <span className="dgoal-card__xp-goal">{goal} XP</span>
        </div>

        <div className="dgoal-card__track">
          <motion.div
            className={`dgoal-card__fill${done ? ' dgoal-card__fill--done' : ''}`}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>

        {done
          ? <p className="dgoal-card__msg dgoal-card__msg--done">{t('categorySelection.goalReachedMsg')}</p>
          : <p className="dgoal-card__msg">{t('categorySelection.xpRemaining', { count: goal - Math.min(dailyXP, goal) })}</p>
        }

        <div className="dgoal-card__goal-picker">
          <p className="dgoal-card__goal-picker-label">{t('categorySelection.dailyPace')}</p>
          <div className="dgoal-card__goal-chips">
            {GOAL_OPTIONS.map(opt => (
              <button
                key={opt.xp}
                className={`dgoal-card__goal-chip${goal === opt.xp ? ' dgoal-card__goal-chip--active' : ''}`}
                onClick={() => onGoalChange(opt.xp)}
              >
                <span className="dgoal-card__goal-chip-label">{t(`categorySelection.goalOptions.${GOAL_OPTION_KEYS[opt.xp]}.label`)}</span>
                <span className="dgoal-card__goal-chip-sub">{t(`categorySelection.goalOptions.${GOAL_OPTION_KEYS[opt.xp]}.sub`)}</span>
              </button>
            ))}
          </div>
        </div>

        <p className="dgoal-card__streak-label"><EmojiText text={t('categorySelection.currentStreak') as string} /></p>
        <div className="dgoal-card__cal">
          {cal.map((d, i) => (
            <div key={i} className="dgoal-card__col">
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                flexShrink: 0,
                background: d.active ? 'var(--color-accent, var(--gold-primary))' : 'transparent',
                border: d.active
                  ? '2.5px solid var(--color-accent, var(--gold-primary))'
                  : d.isFuture
                  ? '2px dashed var(--dg-cal-future)'
                  : '2.5px solid var(--dg-cal-empty)',
                boxShadow: d.isToday ? '0 0 0 3px var(--color-accent, var(--gold-primary))' : 'none',
              }} />
              <span style={{
                fontSize: 10,
                fontWeight: d.isToday ? 800 : 600,
                color: d.isToday ? 'var(--color-accent, var(--gold-primary))' : 'var(--dg-muted)',
                textTransform: 'uppercase',
                lineHeight: 1,
              }}>
                {d.label}
              </span>
            </div>
          ))}
        </div>
        <p className="dgoal-card__streak-count">
          {streak} {t('categorySelection.streakDaysSuffix', { count: streak })}
        </p>
        <div className="dgoal-card__shields">
          <span className="dgoal-card__shields-slots">
            {Array.from({ length: streak >= 50 || shields > 2 ? 4 : 2 }, (_, i) => (
              <span key={i} className={`dgoal-card__shield-slot${i < shields ? ' dgoal-card__shield-slot--active' : ''}`}><Shield size={16} strokeWidth={1.75} /></span>
            ))}
          </span>
          <span className="dgoal-card__shields-label">
            {shields > 0
              ? t('categorySelection.shieldsAvailable', { count: shields })
              : t('categorySelection.earnShieldHint')}
          </span>
        </div>
        <button className="dgoal-card__cta" onClick={onClose}>
          {done ? t('categorySelection.perfect') : t('categorySelection.letsGo')}
        </button>
      </motion.div>
    </motion.div>
  );
};

// ─── Collectible unlock modal ─────────────────────────────────────────────────

const CollectibleModal: React.FC<{ item: CollectibleDef; onClose: () => void }> = ({ item, onClose }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      className="coll-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, pointerEvents: 'none' }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="coll-card"
        initial={{ scale: 0.7, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="coll-card__banner" aria-hidden="true" />
        <motion.span
          className="coll-card__emoji"
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 14, delay: 0.1 }}
        >
          <CollectibleIcon path={item.icon} className="coll-card__icon" />
        </motion.span>
        <motion.p className="coll-card__kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>
          {t('categorySelection.objectUnlocked')}
        </motion.p>
        <motion.h2 className="coll-card__name" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}>
          {item.name}
        </motion.h2>
        <motion.p className="coll-card__desc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.42 }}>
          {item.desc}
        </motion.p>
        <motion.button
          className="coll-card__btn"
          onClick={onClose}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.52 }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
        >
          {t('categorySelection.addToCollection')}
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// ─── Unit Celebration ─────────────────────────────────────────────────────────

interface UnitCelebrationProps {
  unit: typeof UNIT_CONFIGS[0];
  onClose: () => void;
}

const UnitCelebration: React.FC<UnitCelebrationProps> = ({ unit, onClose }) => {
  const { t } = useTranslation();
  return (
  <motion.div
    className="ucelebration-overlay"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.22 }}
    onClick={onClose}
  >
    <motion.div
      className="ucelebration-card"
      initial={{ scale: 0.6, opacity: 0, y: 30 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.85, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      onClick={e => e.stopPropagation()}
    >
      {/* Particules bogolan */}
      {CONFETTI_ITEMS.map(p => {
        const rad = (p.angle * Math.PI) / 180;
        return (
          <motion.div
            key={p.id}
            className="ucelebration-particle"
            initial={{ opacity: 0, x: 0, y: 0, scale: 0, rotate: 0 }}
            animate={{
              opacity: [0, 1, 1, 0],
              x: Math.cos(rad) * p.dist,
              y: Math.sin(rad) * p.dist,
              scale: [0, 1.3, 1, 0.6],
              rotate: p.id % 2 === 0 ? 180 : -180,
            }}
            transition={{ duration: 1.6, delay: p.delay, ease: 'easeOut' }}
            style={{ color: p.color }}
          >
            <BogolonIcon variant={p.variant} size={p.size} />
          </motion.div>
        );
      })}

      {/* Bande décorative du thème */}
      <div className="ucelebration-banner" aria-hidden="true" />

      {/* Icône hiéroglyphe */}
      <motion.span
        className="ucelebration-icon"
        initial={{ scale: 0, rotate: -15 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.15 }}
      >
        {unit.icon}
      </motion.span>

      <motion.p
        className="ucelebration-kicker"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        {t('categorySelection.unitCompleted')}
      </motion.p>
      <motion.h2
        className="ucelebration-title"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
      >
        {displayUnitTitle(unit.id, t)}
      </motion.h2>
      <motion.p
        className="ucelebration-subtitle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55 }}
      >
        {displayUnitSubtitle(unit.id, t)}
      </motion.p>

      <motion.button
        className="ucelebration-btn"
        onClick={onClose}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65 }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
      >
        {t('categorySelection.continueArrow')}
      </motion.button>
    </motion.div>
  </motion.div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const CategorySelection: React.FC<CategorySelectionProps> = ({
  languageCode,
  categories,
  onSelectCategory,
  onStartRevision,
  userProgress,
  completedCategories,
  sessionLearnedWords = new Set<string>(),
  onBackToMenu,
  isMobileView = false,
  onSelectGrammar,
  onSelectConstruction,
  subscriptionTier,
  onOpenSubscription,
  onOpenMiniGame,
  onSelectQuizTheme,
  pendingChallengesCount = 0,
  onSelectConversation,
  activationKey = 0,
  onKamaLessonComplete,
  onKamaAnswer,
  isKamaWordTestable,
}) => {
  const SPLIT_SIZE = 20;

  const { theme } = useTheme();
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedWordRange, setSelectedWordRange] = useState<{ start: number; end: number } | null>(null);
  const [showPreview, setShowPreview]             = useState(false);
  const [lockedToast, setLockedToast]             = useState<string | null>(null);
  const [statsRefreshTrigger, setStatsRefreshTrigger] = useState(0);
  const [celebrationUnitId, setCelebrationUnitId]     = useState<number | null>(null);
  const [dailyXP,  setDailyXP]  = useState(() => getDailyXP(languageCode));
  const [streakCount, setStreakCount] = useState(() => {
    try {
      const raw = localStorage.getItem('jogolinga-daily-streak');
      if (!raw) return 0;
      return JSON.parse(raw).streak ?? 0;
    } catch { return 0; }
  });
  // Indique si la valeur du streak a été confirmée au moins une fois depuis le montage
  const [streakReady, setStreakReady] = useState(() => {
    return !!localStorage.getItem('jogolinga-daily-streak');
  });
  const [convReplayPopup, setConvReplayPopup] = useState<string | null>(null);
  const [collectibleModal, setCollectibleModal] = useState<CollectibleDef | null>(null);
  const [showDailyModal,   setShowDailyModal]   = useState(false);
  const [dailyGoal, setDailyGoalRaw] = useState<number>(
    () => parseInt(localStorage.getItem(GOAL_KEY) ?? '50', 10)
  );
  const setDailyGoal = (xp: number) => {
    localStorage.setItem(GOAL_KEY, String(xp));
    setDailyGoalRaw(xp);
    supabaseProgressService.saveDailyGoalPreference(xp);
  };
  const [shieldCount,      setShieldCount]      = useState(() => readShields());
  const [shieldToast,      setShieldToast]      = useState<string | null>(null);
  const [shieldUsedModal,  setShieldUsedModal]  = useState<{ streak: number; remaining: number } | null>(null);
  const [shieldInfoModal,  setShieldInfoModal]  = useState(false);
  const shownCollectiblesKey = 'jogolinga-shown-collectibles';
  const getShownIds = (): Set<string> => {
    try { return new Set(JSON.parse(localStorage.getItem(shownCollectiblesKey) || '[]')); }
    catch { return new Set(); }
  };

  // Toutes les categories sauf Grammaire (affichee comme checkpoint)
  const categoryList = useMemo(
    () => Object.keys(categories).filter(c => c !== 'Grammaire'),
    [categories]
  );

  // ── Progress loading (identique a l'ancienne version) ──────────────────────

  const loadLearnedWordsFromStorage = useCallback(
    (targetCategory: string): Set<string> => {
      const learnedWords = new Set<string>();
      try {
        if (userProgress?.recentlyLearnedWords?.length) {
          userProgress.recentlyLearnedWords.forEach(item => {
            if (item.category === targetCategory) {
              learnedWords.add(item.word);
              learnedWords.add(cleanWord(item.word));
            }
          });
        }
        const keys = [
          `${languageCode}-${targetCategory}-learnedWords`,
          `${languageCode}-${targetCategory}-sessionLearned`,
          `${languageCode}-allLearnedWords`,
        ];
        keys.forEach(k => {
          const raw = localStorage.getItem(k);
          if (raw) {
            (JSON.parse(raw) as string[]).forEach(w => {
              learnedWords.add(w);
              learnedWords.add(cleanWord(w));
            });
          }
        });
      } catch {}
      return learnedWords;
    },
    [languageCode, userProgress]
  );

  const isWordLearned = useCallback(
    (word: string, learned: Set<string>): boolean => {
      const base = cleanWord(word);
      return [word, base, word.toLowerCase(), base.toLowerCase()].some(v =>
        learned.has(v)
      );
    },
    []
  );

  // Mirrors the exclusions applied when words are actually recorded as
  // "learned" (App.tsx's reviewableWords/fullCategoryWordKeys): section
  // headers and, within Kama's "Lire les hiéroglyphes", narrative/exercise
  // cards. Without this, the denominator here counts cards that can never
  // become "learned", capping a chunk's progress below 100% forever (e.g.
  // stuck at "15/20").
  const getVocabWordKeys = useCallback(
    (cat: string): string[] =>
      Object.keys(categories[cat])
        .filter(k => !k.includes('_'))
        .filter(k => !(categories[cat] as Record<string, WordData>)[k]?.isSectionHeader)
        .filter(k => !(cat === 'Lire les hiéroglyphes' && KAMA_NON_VOCAB_KEYS_IN_LECTURE.has(k))),
    [categories]
  );

  const categoryStats = useMemo(() => {
    const stats: Record<string, { total: number; learned: number; percentage: number }> = {};
    categoryList.forEach(cat => {
      const words = getVocabWordKeys(cat);
      const learned = loadLearnedWordsFromStorage(cat);
      // Kama's narrative/grammar categories ("Phrase", "Lecture analytique",
      // "Sur les murs des temples") are deliberately never recorded as
      // learned words (App.tsx's handleKamaLessonComplete — RevisionMode
      // must not quiz them), so counting learned words kept them at 0 and
      // their trail node never turned "done". Their completion is recorded
      // in completedCategories instead once the lesson is read through.
      const learnedCount =
        languageCode === 'ka' && KAMA_NON_VOCAB_CATEGORIES.has(cat) && completedCategories?.includes(cat)
          ? words.length
          : words.reduce((n, w) => n + (isWordLearned(w, learned) ? 1 : 0), 0);
      stats[cat] = {
        total: words.length,
        learned: learnedCount,
        percentage: words.length > 0 ? Math.round((learnedCount / words.length) * 100) : 0,
      };
    });
    return stats;
  }, [categoryList, categories, getVocabWordKeys, loadLearnedWordsFromStorage, isWordLearned, statsRefreshTrigger, languageCode, completedCategories]);

  // ── Unit helpers ───────────────────────────────────────────────────────────

  // Filtrer les categories qui existent vraiment dans cette langue
  const unitCategories = useCallback(
    (unitCats: string[]) => unitCats.filter(c => categoryList.includes(c)),
    [categoryList]
  );

  const unitProgress = useCallback(
    (unitCats: string[]): number => {
      const cats = unitCategories(unitCats);
      if (!cats.length) return 0;
      const total = cats.reduce((s, c) => s + (categoryStats[c]?.percentage ?? 0), 0);
      return Math.round(total / cats.length);
    },
    [unitCategories, categoryStats]
  );

  const isUnitUnlocked = useCallback(
    (unitIndex: number): boolean => {
      if (unitIndex === 0) return true;
      const prev = UNIT_CONFIGS[unitIndex - 1];
      return unitProgress(prev.categories) >= UNLOCK_THRESHOLD;
    },
    [unitProgress]
  );

  // ── Interaction ────────────────────────────────────────────────────────────

  const handleCategoryClick = useCallback(
    (category: string, unitIndex: number, wordStart?: number, wordEnd?: number) => {
      if (!isUnitUnlocked(unitIndex)) {
        const prevUnit = UNIT_CONFIGS[unitIndex - 1];
        const needed = UNLOCK_THRESHOLD - unitProgress(prevUnit.categories);
        setLockedToast(
          `Complete l'unite ${prevUnit.id} a ${UNLOCK_THRESHOLD}% pour debloquer (encore ${needed}%)`
        );
        return;
      }
      setSelectedCategory(category);
      setSelectedWordRange(wordStart !== undefined && wordEnd !== undefined ? { start: wordStart, end: wordEnd } : null);
      setShowPreview(true);
    },
    [isUnitUnlocked, unitProgress]
  );

  useLayoutEffect(() => {
    if (activeNodeRef.current) {
      activeNodeRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);



  useEffect(() => {
    if (!lockedToast) return;
    const t = setTimeout(() => setLockedToast(null), 3000);
    return () => clearTimeout(t);
  }, [lockedToast]);

  useEffect(() => {
    if (!shieldToast) return;
    const t = setTimeout(() => setShieldToast(null), 5000);
    return () => clearTimeout(t);
  }, [shieldToast]);

  // Rafraîchir constructionStats quand les données de phrases sont chargées depuis Supabase
  useEffect(() => {
    const handleSentenceSync = () => {
      setStatsRefreshTrigger(n => n + 1);
    };
    window.addEventListener('sentenceProgressSynced', handleSentenceSync);
    return () => window.removeEventListener('sentenceProgressSynced', handleSentenceSync);
  }, [languageCode]);

  // Mise à jour XP journalier, streak et boucliers en temps réel
  useEffect(() => {
    const refresh = (e?: Event) => {
      setDailyXP(getDailyXP(languageCode));
      try {
        const raw = localStorage.getItem('jogolinga-daily-streak');
        const s = raw ? (JSON.parse(raw).streak ?? 0) : 0;
        setStreakCount(s);
        setStreakReady(true);
      } catch { /* ignore */ }
      setShieldCount(readShields());

      if (e instanceof CustomEvent) {
        const { shieldUsed, shieldAwarded, shields: newShields, streak: evtStreak, source } =
          (e.detail ?? {}) as { shieldUsed?: boolean; shieldAwarded?: boolean; shields?: number; streak?: number; source?: string };
        if (shieldUsed) {
          setShieldUsedModal({ streak: evtStreak ?? 0, remaining: newShields ?? 0 });
        }
        if (shieldAwarded) {
          const count = newShields ?? 1;
          setShieldToast(t('categorySelection.shieldEarnedToast', { count }));
        }
        // After Supabase sync, localStorage is freshly populated — recompute all
        // categoryStats/trailSteps so validated units (checkmarks) appear correctly.
        if (source === 'load_progress') setStatsRefreshTrigger(t => t + 1);
      }
    };
    // Lecture immédiate au montage ET à chaque changement de langue
    refresh();
    window.addEventListener('xpUpdated',    refresh);
    window.addEventListener('streakUpdated', refresh);
    return () => {
      window.removeEventListener('xpUpdated',    refresh);
      window.removeEventListener('streakUpdated', refresh);
    };
  }, [languageCode, t]);

  const handleStartLearning = useCallback(() => {
    if (selectedCategory) onSelectCategory(selectedCategory, selectedWordRange ?? undefined);
  }, [selectedCategory, selectedWordRange, onSelectCategory]);

  const handleStartRevision = useCallback(() => {
    if (selectedCategory) onStartRevision(selectedCategory);
  }, [selectedCategory, onStartRevision]);

  const handleBackFromPreview = useCallback(() => {
    setShowPreview(false);
    setSelectedCategory(null);
    setSelectedWordRange(null);
  }, []);

  // ── Grammar & Construction progress (from localStorage) ───────────────────

  const grammarStats = useMemo(() => {
    // Source 1 : sessions terminées (ont la sous-catégorie → filtrage par unité)
    let allSessions: { subcategory: string; masteredWords: { word: string }[] }[] = [];
    try {
      const raw = localStorage.getItem(`grammar-progress-${languageCode}`);
      if (raw) allSessions = JSON.parse(raw);
    } catch {}

    const result = [1, 2, 3].reduce((acc, unit) => {
      const unitSessions = allSessions.filter(s => grammarSubMatchesUnit(s.subcategory, unit));
      const unitWords = new Set<string>();
      unitSessions.forEach(s => s.masteredWords?.forEach(w => unitWords.add(w.word)));
      acc[unit] = { sessions: unitSessions.length, rulesLearned: unitWords.size };
      return acc;
    }, {} as Record<number, { sessions: number; rulesLearned: number }>);

    // Source 2 : recentlyLearnedWords (sans info d'unité → fallback sur unité 1)
    const progressWords = (userProgress?.recentlyLearnedWords || [])
      .filter(item => item.category === 'Grammaire');
    if (progressWords.length > 0 && result[1].rulesLearned === 0) {
      result[1] = { sessions: 0, rulesLearned: progressWords.length };
    }

    return result;
  }, [languageCode, userProgress]);

  // One counter per unit of UNIT_SENTENCE_CATEGORIES — hard-coded to units
  // 1-3 before, so a sentence mastered in a 4th unit hit an undefined Set
  // and the catch below zeroed every unit's count.
  const emptyByUnit = (): Record<number, number> =>
    Object.fromEntries(UNIT_SENTENCE_CATEGORIES.map((_, i) => [i + 1, 0]));

  const constructionStats = useMemo(() => {
    try {
      const raw = localStorage.getItem(`sentence-progress-${languageCode}`);
      if (!raw) return { total: { sessions: 0, sentencesDone: 0 }, byUnit: emptyByUnit() };
      const sessions = JSON.parse(raw) as { category?: string; masteredSentences?: { original?: string; category?: string }[] }[];

      // Dédupliquer globalement et par unité
      const uniqueAll = new Set<string>();
      const uniqueByUnit: Record<number, Set<string>> = Object.fromEntries(
        UNIT_SENTENCE_CATEGORIES.map((_, i) => [i + 1, new Set<string>()])
      );

      sessions.forEach(s => s.masteredSentences?.forEach(m => {
        if (!m.original) return;
        uniqueAll.add(m.original);
        // Utiliser la catégorie de la phrase en priorité, sinon celle de la session
        const cat = (m.category || s.category || '').trim();
        if (!cat) return;
        // Trouver l'unité correspondant à la catégorie de la phrase
        const unitIdx = UNIT_SENTENCE_CATEGORIES.findIndex(cats =>
          cats.some(c => cat.toLowerCase().includes(c.toLowerCase()))
        );
        if (unitIdx >= 0) uniqueByUnit[unitIdx + 1].add(m.original);
      }));

      return {
        total: { sessions: sessions.length, sentencesDone: uniqueAll.size },
        byUnit: Object.fromEntries(Object.entries(uniqueByUnit).map(([u, set]) => [u, set.size])) as Record<number, number>,
      };
    } catch { return { total: { sessions: 0, sentencesDone: 0 }, byUnit: emptyByUnit() }; }
  }, [languageCode, statsRefreshTrigger]);

  // Nombre total de phrases par unité, calculé depuis les données réelles
  const sentTotalByUnit = useMemo<Record<number, number>>(() => {
    try {
      const langData = getLanguageData(languageCode as any);
      const sentences = langData.sentencesToConstruct || [];
      return UNIT_SENTENCE_CATEGORIES.reduce((acc, cats, idx) => {
        const unitNum = idx + 1;
        const total = new Set(sentences.filter(s => cats.includes(s.category)).map(s => s.original)).size;
        acc[unitNum] = total;
        return acc;
      }, {} as Record<number, number>);
    } catch {
      return { 1: 9, 2: 9, 3: 9 };
    }
  }, [languageCode]);

  const hasGrammar = Object.keys(categories).includes('Grammaire');

  // Compte les mots réels par unité de grammaire (les clés top-level sont des sous-catégories)
  const grammarWordsByUnit = useMemo(() => {
    if (!hasGrammar) return { 1: 0, 2: 0, 3: 0 } as Record<number, number>;
    const grammarCats = categories['Grammaire'] as Record<string, any>;
    return [1, 2, 3].reduce((acc, unit) => {
      const count = Object.entries(grammarCats)
        .filter(([k]) => grammarSubMatchesUnit(k, unit))
        .reduce((n, [, v]) => n + Object.keys(v).filter(k => !k.includes('_')).length, 0);
      acc[unit] = count;
      return acc;
    }, {} as Record<number, number>);
  }, [categories, hasGrammar]);

  // Mots appris par sous-catégorie de grammaire
  const grammarLearnedBySubcat = useMemo(() => {
    const result: Record<string, number> = {};
    try {
      const raw = localStorage.getItem(`grammar-progress-${languageCode}`);
      if (!raw) return result;
      const allSessions = JSON.parse(raw) as { subcategory: string; masteredWords: { word: string }[] }[];
      allSessions.forEach(s => {
        if (!s.subcategory) return;
        const count = new Set(s.masteredWords?.map(w => w.word) ?? []).size;
        result[s.subcategory] = Math.max(result[s.subcategory] ?? 0, count);
      });
    } catch {}
    return result;
  }, [languageCode, statsRefreshTrigger]);

  // Total de phrases par catégorie de phrase (clés = UNIT_SENTENCE_CATEGORIES)
  const sentTotalByCategory = useMemo(() => {
    const result: Record<string, number> = {};
    try {
      const langData = getLanguageData(languageCode as any);
      const sentences = langData.sentencesToConstruct || [];
      UNIT_SENTENCE_CATEGORIES.forEach(unitCats => {
        unitCats.forEach(catName => {
          result[catName] = new Set(
            sentences.filter(s => s.category === catName).map(s => s.original)
          ).size;
        });
      });
    } catch {}
    return result;
  }, [languageCode]);

  // Phrases faites par catégorie de phrase
  const constructionDoneBySubcat = useMemo(() => {
    const result: Record<string, number> = {};
    try {
      const raw = localStorage.getItem(`sentence-progress-${languageCode}`);
      if (!raw) return result;
      const sessions = JSON.parse(raw) as { masteredSentences?: { original?: string; category?: string }[]; category?: string }[];
      const uniqueBySubcat: Record<string, Set<string>> = {};
      sessions.forEach(s => s.masteredSentences?.forEach(m => {
        if (!m.original) return;
        const cat = (m.category || s.category || '').trim();
        UNIT_SENTENCE_CATEGORIES.forEach(unitCats => {
          unitCats.forEach(subcatName => {
            if (cat.toLowerCase().includes(subcatName.toLowerCase())) {
              if (!uniqueBySubcat[subcatName]) uniqueBySubcat[subcatName] = new Set();
              uniqueBySubcat[subcatName].add(m.original!);
            }
          });
        });
      }));
      Object.entries(uniqueBySubcat).forEach(([subcat, set]) => { result[subcat] = set.size; });
    } catch {}
    return result;
  }, [languageCode, statsRefreshTrigger]);

  // ── Global stats ───────────────────────────────────────────────────────────

  const vocabLearned        = Object.values(categoryStats).reduce((s, st) => s + st.learned, 0);
  const vocabWords          = Object.values(categoryStats).reduce((s, st) => s + st.total, 0);
  const grammarLearnedTotal = Object.values(grammarStats).reduce((s, u) => s + u.rulesLearned, 0);
  const grammarWordsTotal   = Object.values(grammarWordsByUnit).reduce((s, n) => s + n, 0);
  const totalSentLearned    = constructionStats.total.sentencesDone;
  const totalSentWords      = Object.values(sentTotalByUnit).reduce((s, n) => s + n, 0);
  // "Mots appris" = vocab + grammaire seulement (les phrases sont affichées séparément)
  const totalLearned        = vocabLearned + grammarLearnedTotal;
  const totalWords          = vocabWords + grammarWordsTotal + totalSentWords;
  const globalPct           = totalWords > 0 ? Math.min(100, Math.round(((totalLearned + totalSentLearned) / totalWords) * 100)) : 0;


  // ── Trail step types
  type StepStatus = 'done' | 'in_progress' | 'available' | 'locked';
  interface TrailStep {
    id: string;
    label: string;
    status: StepStatus;
    learned: number;
    total: number;
    onClick: () => void;
    lockedByPremium?: boolean;
    lockedByGuide?: boolean;
    wordStart?: number;
    wordEnd?: number;
    unitId: number;
    /** Fin de catégorie reportée : catégorie après laquelle elle est placée. */
    anchorCategory?: string;
  }

  // ── Landmarks dynamiques : scènes calculées selon le nombre de chansons
  const trailLandmarks = useMemo<TrailLandmark[]>(() => {
    const songs = SONGS_BY_LANGUAGE[languageCode] ?? [];
    const sceneCount = Math.ceil(songs.length / SCENE_MAX_SONGS);
    const scenes: TrailLandmark[] = Array.from({ length: sceneCount }, (_, i) => ({
      atStepIndex: 13 + i * 5,
      emoji: '🎵',
      label: t('categorySelection.sceneLabel'),
      sublabel: t('categorySelection.sceneSublabel'),
      game: 'music' as const,
    }));
    return scenes;
  }, [languageCode, t]);

  // ── Build flat trail steps
  const trailSteps = useMemo<TrailStep[]>(() => {
    const steps: TrailStep[] = [];
    let grammarStepNum = 0;
    let phraseStepNum  = 0;
    // Essais gratuits : seul le PREMIER nœud éligible de chaque type peut être essayé
    const isFreeUser = subscriptionTier !== 'premium';
    const grammarTrialsLeft = isFreeUser
      ? Math.max(0, 3 - parseInt(localStorage.getItem('jogolinga-trial-grammar') || '0', 10))
      : Infinity;
    const sentencesTrialsLeft = isFreeUser
      ? Math.max(0, 3 - parseInt(localStorage.getItem('jogolinga-trial-sentences') || '0', 10))
      : Infinity;
    let grammarTrialGranted = false;
    let phrasesTrialGranted = false;
    UNIT_CONFIGS.forEach((unit, unitIndex) => {
      const cats = unitCategories(unit.categories);
      const unlocked = isUnitUnlocked(unitIndex);
      const unitNum = unitIndex + 1;
      const unitStart = steps.length; // début des étapes de cette unité
      // Compteurs locaux pour les labels (remis à 0 par unité)
      let unitGrammarNum = 0;
      let unitPhraseNum  = 0;

      // Une partie (plage de mots) d'une catégorie découpée
      const makeVocabPart = (cat: string, p: number, range: { start: number; end: number }): TrailStep => {
        const learnedSet = loadLearnedWordsFromStorage(cat);
        const partKeys = getVocabWordKeys(cat).slice(range.start, range.end);
        const partLearned = partKeys.reduce((n, w) => n + (isWordLearned(w, learnedSet) ? 1 : 0), 0);
        const partPct = partKeys.length > 0 ? Math.round((partLearned / partKeys.length) * 100) : 0;
        let status: StepStatus;
        if (!unlocked) status = 'locked';
        else if (partPct === 100) status = 'done';
        else if (partLearned > 0) status = 'in_progress';
        else status = 'available';
        return {
          id: `vocab-${cat}-part${p}`,
          label: `${displayCategoryLabel(cat, t)} · ${p + 1}`,
          status,
          learned: partLearned,
          total: partKeys.length,
          wordStart: range.start,
          wordEnd: range.end,
          unitId: unit.id,
          onClick: () => handleCategoryClick(cat, unitIndex, range.start, range.end),
        };
      };
      const categoryKeys = (cat: string) => ({
        raw: Object.keys(categories[cat] ?? {}).filter(k => !k.includes('_')),
        vocab: getVocabWordKeys(cat),
      });
      const presentInUnit = (unitId: number) =>
        unitCategories(UNIT_CONFIGS.find(u => u.id === unitId)?.categories ?? []);

      cats.forEach(cat => {
        const stats = categoryStats[cat] ?? { total: 0, learned: 0, percentage: 0 };

        if (stats.total > SPLIT_SIZE || LESSON_BREAKS[cat]) {
          // Parties équilibrées d'au plus SPLIT_SIZE mots — ou une par
          // chapitre pour LESSON_BREAKS ; la fin des très grosses catégories
          // est reportée plus loin (utils/trailSplit)
          vocabSplit(languageCode, cat, stats.total, presentInUnit, categoryKeys(cat)).head
            .forEach((range, p) => steps.push(makeVocabPart(cat, p, range)));
        } else {
          let status: StepStatus;
          if (!unlocked) status = 'locked';
          else if (stats.percentage === 100) status = 'done';
          else if (stats.learned > 0) status = 'in_progress';
          else status = 'available';
          steps.push({
            id: `vocab-${cat}`,
            label: displayCategoryLabel(cat, t),
            status,
            learned: stats.learned,
            total: stats.total,
            unitId: unit.id,
            onClick: () => handleCategoryClick(cat, unitIndex),
          });
        }
      });

      // ── Fins de catégories reportées dans cette unité
      categoryList.forEach(cat => {
        const total = categoryStats[cat]?.total ?? 0;
        if (total <= SPLIT_SIZE) return;
        const split = vocabSplit(languageCode, cat, total, presentInUnit, categoryKeys(cat));
        if (!split.tailAnchor || split.tailAnchor.unitId !== unit.id) return;
        const anchor = split.tailAnchor.afterCategory;
        let at = steps.length;
        for (let i = steps.length - 1; i >= unitStart; i--) {
          const st = steps[i];
          if (st.id === `vocab-${anchor}` || st.id.startsWith(`vocab-${anchor}-part`) || st.anchorCategory === anchor) { at = i + 1; break; }
        }
        const tailSteps = split.tail.map((range, i) => ({
          ...makeVocabPart(cat, split.head.length + i, range),
          anchorCategory: anchor,
        }));
        steps.splice(at, 0, ...tailSteps);
      });

      // ── Conversation steps (inserted after designated vocab categories)
      if (onSelectConversation) {
        cats.forEach(cat => {
          const convConfig = CONVERSATION_TRAIL_CONFIGS.find(
            c => c.afterCategory === cat && c.unitId === unit.id && c.languageCodes.includes(languageCode)
          );
          if (!convConfig) return;
          const convList = CONVERSATIONS_BY_LANGUAGE[languageCode] ?? [];
          const conv = convList.find(c => c.id === convConfig.conversationId);
          const convTotal = conv?.steps.length ?? 0;
          const convDone = readConvDone(languageCode, convConfig.conversationId);
          let status: StepStatus;
          if (!unlocked) status = 'locked';
          else if (convTotal > 0 && convDone >= convTotal) status = 'done';
          else if (convDone > 0) status = 'in_progress';
          else status = 'available';

          // Find index of last vocab step for this category and insert after it
          const insertIdx = (() => {
            for (let i = steps.length - 1; i >= 0; i--) {
              if (steps[i].id === `vocab-${cat}` || steps[i].id.startsWith(`vocab-${cat}-part`)) return i + 1;
            }
            return steps.length;
          })();

          steps.splice(insertIdx, 0, {
            id: `conversation-${convConfig.conversationId}`,
            label: t('categorySelection.conversationLabel'),
            status,
            learned: convDone,
            total: convTotal,
            unitId: unit.id,
            onClick: () => {
              if (!unlocked) { setLockedToast(t('categorySelection.completeUnitToUnlock', { unit: unitIndex })); return; }
              if (status === 'done') {
                setConvReplayPopup(prev => prev === convConfig.conversationId ? null : convConfig.conversationId);
                return;
              }
              onSelectConversation(convConfig.conversationId);
            },
          });
        });
      }

      if (hasGrammar && onSelectGrammar) {
        const grammarCatsObj = categories['Grammaire'] as Record<string, any>;
        const unitSubcats = Object.entries(grammarCatsObj)
          .filter(([k]) => grammarSubMatchesUnit(k, unitNum));

        // Grouper les sous-catégories en parties de ~SPLIT_SIZE mots
        const grammarParts: Array<{ subcats: string[]; total: number }> = [];
        let curSubcats: string[] = [];
        let curTotal = 0;
        unitSubcats.forEach(([subcatName, subcatWords]) => {
          const wc = Object.keys(subcatWords as object).filter(k => !k.includes('_')).length;
          if (curTotal + wc > SPLIT_SIZE && curSubcats.length > 0) {
            grammarParts.push({ subcats: [...curSubcats], total: curTotal });
            curSubcats = [subcatName];
            curTotal = wc;
          } else {
            curSubcats.push(subcatName);
            curTotal += wc;
          }
        });
        if (curSubcats.length > 0) grammarParts.push({ subcats: curSubcats, total: curTotal });

        const unitGrammarSteps: TrailStep[] = [];
        grammarParts.forEach((part) => {
          grammarStepNum++;
          unitGrammarNum++;
          // Seul le premier nœud grammaire éligible reçoit l'accès trial
          const canTrial = isFreeUser && unlocked && !grammarTrialGranted && grammarTrialsLeft > 0;
          if (canTrial) grammarTrialGranted = true;
          const partLearned = part.subcats.reduce((n, s) => n + (grammarLearnedBySubcat[s] ?? 0), 0);
          const capped = Math.min(partLearned, part.total);
          const partPct = part.total > 0 ? Math.round((capped / part.total) * 100) : 0;
          let status: StepStatus;
          if (!unlocked || isFreeUser) status = 'locked';
          else if (partPct === 100) status = 'done';
          else if (partLearned > 0) status = 'in_progress';
          else status = 'available';
          unitGrammarSteps.push({
            id: `grammar-global-${grammarStepNum}`,
            label: t('categorySelection.grammarPart', { n: unitGrammarNum }),
            status,
            learned: capped,
            total: part.total,
            unitId: unit.id,
            lockedByPremium: isFreeUser && unlocked,
            onClick: () => {
              if (!unlocked) { setLockedToast(t('categorySelection.completeUnitToUnlock', { unit: unitIndex })); return; }
              if (isFreeUser && !canTrial) { onOpenSubscription?.(); return; }
              onSelectGrammar(unitNum, part.subcats);
            },
          });
        });

        // Intercaler la grammaire entre les catégories de vocabulaire
        // (même répartition que la navigation « Suivant », utils/trailSequence)
        const slots = grammarSlots(cats.length, unitGrammarSteps.length);
        const unitSteps = steps.splice(unitStart);
        const belongsTo = (step: TrailStep, cat: string) =>
          step.anchorCategory === cat || step.id === `vocab-${cat}` || step.id.startsWith(`vocab-${cat}-part`) ||
          CONVERSATION_TRAIL_CONFIGS.some(c => step.id === `conversation-${c.conversationId}`
            && c.afterCategory === cat && c.unitId === unit.id);
        const placed = new Set<TrailStep>();
        cats.forEach((cat, k) => {
          unitSteps.filter(st => belongsTo(st, cat)).forEach(st => { steps.push(st); placed.add(st); });
          unitGrammarSteps.filter((_, j) => slots[j] === k).forEach(g => steps.push(g));
        });
        unitSteps.filter(st => !placed.has(st)).forEach(st => steps.push(st));
        unitGrammarSteps.filter((_, j) => slots[j] === -1).forEach(g => steps.push(g));
      }

      if (onSelectConstruction) {
        const unitSentCats = UNIT_SENTENCE_CATEGORIES[unitIndex] ?? [];

        // Grouper les catégories de phrases en parties de ~SPLIT_SIZE phrases.
        // Les catégories sans aucune phrase dans cette langue sont ignorées :
        // sinon elles formaient seules une étape « Phrases · 2 » à 0 phrase
        // (même règle que trailSequence.ts).
        const phraseParts: Array<{ cats: string[]; total: number }> = [];
        let curCats: string[] = [];
        let curPhrTotal = 0;
        unitSentCats.filter(cat => (sentTotalByCategory[cat] ?? 0) > 0).forEach(cat => {
          const catTotal = sentTotalByCategory[cat] ?? 0;
          if (curPhrTotal + catTotal > SPLIT_SIZE && curCats.length > 0) {
            phraseParts.push({ cats: [...curCats], total: curPhrTotal });
            curCats = [cat];
            curPhrTotal = catTotal;
          } else {
            curCats.push(cat);
            curPhrTotal += catTotal;
          }
        });
        if (curCats.length > 0) phraseParts.push({ cats: curCats, total: curPhrTotal });
        // Kama : une étape par thème (Présentation, Famille et relations, Au
        // marché…), nommée d'après lui, plutôt que des regroupements de ~20
        // phrases qui mêlaient deux thèmes dans une même étape.
        if (languageCode === 'ka') {
          phraseParts.splice(0, phraseParts.length, ...unitSentCats
            .filter(cat => (sentTotalByCategory[cat] ?? 0) > 0)
            .map(cat => ({ cats: [cat], total: sentTotalByCategory[cat] })));
        }

        // Fallback si sentTotalByCategory ne donne rien
        if (phraseParts.length === 0 || phraseParts.every(p => p.total === 0)) {
          // Unit 4 ("Panthéon et royaume") has no entry in UNIT_SENTENCE_
          // CATEGORIES (only 3 units are mapped) for any language, so
          // sentTotalByUnit[4] is always undefined here — the `?? 9`
          // placeholder assumes "real content is coming", which is a
          // reasonable guess for languages that do have sentence data
          // elsewhere, but for one with none at all (sentencesToConstruct
          // empty, e.g. Kama) it's just a permanently-locked node with
          // nothing behind it. Only show the placeholder when this
          // language's other units prove it actually has sentence data.
          // Also gated on unitIndex being within UNIT_SENTENCE_CATEGORIES'
          // range: unit 4+ has no entry there at all (for ANY language), so
          // sentTotalByUnit[unitNum] is always undefined for it — without
          // this check, giving any language real sentence data anywhere
          // makes hasAnySentenceData true and this placeholder wrongly
          // fires for unit 4 too, producing a "Phrases · 9 mots" node with
          // nothing real behind it (confirmed happening for Kama).
          const hasAnySentenceData = Object.values(sentTotalByUnit).some(n => n > 0);
          // …and only for a unit that has vocabulary in this language: unit 4
          // is mapped for Kama alone, other languages show nothing there.
          const hasCategoryMapping = unitIndex < UNIT_SENTENCE_CATEGORIES.length && cats.length > 0;
          const sentTotal = sentTotalByUnit[unitNum] ?? (hasCategoryMapping && hasAnySentenceData ? 9 : 0);
          const sentDone  = constructionStats.byUnit[unitNum] ?? 0;
          const sentPct   = sentTotal > 0 ? Math.round((sentDone / sentTotal) * 100) : 0;
          const canTrialPhrases = isFreeUser && unlocked && !phrasesTrialGranted && sentencesTrialsLeft > 0;
          if (canTrialPhrases) phrasesTrialGranted = true;
          let status: StepStatus;
          if (!unlocked || isFreeUser) status = 'locked';
          else if (sentPct === 100) status = 'done';
          else if (sentDone > 0) status = 'in_progress';
          else status = 'available';
          // Only a real practice node if the language actually has sentence-
          // construction data for this unit — otherwise this fallback (no
          // per-category totals) defaults to `sentTotalByUnit[unitNum] ?? 9`,
          // which still resolves to a real 0 when the language's
          // sentencesToConstruct is empty (e.g. Kama), producing a
          // permanently empty "Phrases" node with nothing to click into.
          if (sentTotal > 0) {
            unitPhraseNum++;
            steps.push({
              id: `phrases-${unitNum}`,
              label: t('categorySelection.phrasesLabel'),
              status,
              learned: sentDone,
              total: sentTotal,
              unitId: unit.id,
              lockedByPremium: isFreeUser && unlocked,
              onClick: () => {
                if (!unlocked) { setLockedToast(t('categorySelection.completeUnitToUnlock', { unit: unitIndex })); return; }
                if (isFreeUser && !canTrialPhrases) { onOpenSubscription?.(); return; }
                onSelectConstruction(unitSentCats);
              },
            });
          }
        } else {
          phraseParts.forEach((part) => {
            phraseStepNum++;
            unitPhraseNum++;
            const partDone = part.cats.reduce((n, c) => n + (constructionDoneBySubcat[c] ?? 0), 0);
            const capped   = Math.min(partDone, part.total);
            const partPct  = part.total > 0 ? Math.round((capped / part.total) * 100) : 0;
            const canTrialPhrases = isFreeUser && unlocked && !phrasesTrialGranted && sentencesTrialsLeft > 0;
            if (canTrialPhrases) phrasesTrialGranted = true;
            let status: StepStatus;
            if (!unlocked || isFreeUser) status = 'locked';
            else if (partPct === 100) status = 'done';
            else if (partDone > 0) status = 'in_progress';
            else status = 'available';
            steps.push({
              id: `phrases-global-${phraseStepNum}`,
              label: languageCode === 'ka'
                ? `${t('categorySelection.phrasesLabel')} · ${KAMA_SENTENCE_THEME_LABELS[part.cats[0]] ?? part.cats[0]}`
                : t('categorySelection.phrasesPart', { n: unitPhraseNum }),
              status,
              learned: capped,
              total: part.total,
              unitId: unit.id,
              lockedByPremium: isFreeUser && unlocked,
              onClick: () => {
                if (!unlocked) { setLockedToast(t('categorySelection.completeUnitToUnlock', { unit: unitIndex })); return; }
                if (isFreeUser && !canTrialPhrases) { onOpenSubscription?.(); return; }
                onSelectConstruction(part.cats);
              },
            });
          });
        }
      }

      // Steps quiz culturels pour cette unité
      QUIZ_THEMES_ON_TRAIL.filter(q => q.unitId === unit.id).forEach(({ theme, emoji: _emoji }) => {
        const best = readQuizBestScore(languageCode, theme);
        const inProgress = best === 0 ? readQuizInProgressIndex(languageCode, theme) : 0;
        const effectiveLearned = best > 0 ? best : inProgress;
        let status: StepStatus;
        if (!unlocked) status = 'locked';
        else if (best >= QUIZ_TOTAL) status = 'done';
        else if (effectiveLearned > 0) status = 'in_progress';
        else status = 'available';
        steps.push({
          id: `quiz-${theme}`,
          label: displayQuizThemeLabel(theme, t),
          status,
          learned: effectiveLearned,
          total: QUIZ_TOTAL,
          unitId: unit.id,
          onClick: () => {
            if (!unlocked) { setLockedToast(t('categorySelection.completeUnitToUnlock', { unit: unitIndex })); return; }
            onSelectQuizTheme?.(theme);
          },
        });
      });
    });

    // ── Guided soft path désactivé temporairement ──

    return steps;
  }, [unitCategories, categoryList, isUnitUnlocked, categoryStats, grammarStats, grammarWordsByUnit,
      constructionStats, sentTotalByUnit, hasGrammar, onSelectGrammar, onSelectConstruction,
      subscriptionTier, handleCategoryClick, setLockedToast,
      categories, getVocabWordKeys, loadLearnedWordsFromStorage, isWordLearned,
      grammarLearnedBySubcat, sentTotalByCategory, constructionDoneBySubcat,
      languageCode, onSelectQuizTheme, onSelectConversation, t]);

  const continueStep = useMemo(
    () => trailSteps.find(s => s.status === 'in_progress' && !s.id.startsWith('quiz-')) ??
          trailSteps.find(s => s.status === 'available'   && !s.id.startsWith('quiz-')),
    [trailSteps]
  );

  // Premier nœud disponible APRÈS le nœud actif → signal "suivant"
  const nextStep = useMemo(() => {
    const continueIdx = trailSteps.findIndex(s => s.id === continueStep?.id);
    if (continueIdx === -1) return null;
    return trailSteps.slice(continueIdx + 1).find(s => s.status === 'available') ?? null;
  }, [trailSteps, continueStep]);


  // Steps affichés comme rangées (quiz steps → cartes latérales, pas de rangée propre)
  const displayTrailSteps = useMemo(
    () => trailSteps.filter(s => !s.id.startsWith('quiz-')),
    [trailSteps]
  );

  // The step right after the open Kama lesson, offered on its done page
  // (KamaLearnMode's « Leçon suivante »). Only an unlocked step: a locked
  // or Premium-gated one is left to the trail itself.
  const nextKamaLesson = useMemo(() => {
    if (languageCode !== 'ka' || !selectedCategory) return undefined;
    const current = displayTrailSteps.findIndex(s =>
      selectedWordRange
        ? s.id.startsWith(`vocab-${selectedCategory}-part`) && s.wordStart === selectedWordRange.start
        : s.id === `vocab-${selectedCategory}`
    );
    const next = current >= 0 ? displayTrailSteps[current + 1] : undefined;
    if (!next || next.status === 'locked' || next.lockedByPremium || next.lockedByGuide) return undefined;
    return {
      label: next.label,
      onStart: () => {
        handleBackFromPreview();
        next.onClick();
      },
    };
  }, [languageCode, selectedCategory, selectedWordRange, displayTrailSteps, handleBackFromPreview]);

  // Map : id du step hôte → quiz step qui s'affiche à côté de lui
  const quizBesideStep = useMemo(() => {
    const map = new Map<string, TrailStep>();
    trailSteps.forEach((s, i) => {
      if (s.id.startsWith('quiz-') && i > 0) map.set(trailSteps[i - 1].id, s);
    });
    return map;
  }, [trailSteps]);

  // Jalons aux frontières d'unités : on détecte le changement de unitId dans le
  // tableau plat, ce qui garantit que le jalon est au PREMIER pas de l'unité
  // (vocab, grammaire ou phrases selon ce qui vient en premier) et pas uniquement
  // au premier pas vocab — donc cohérent quelle que soit la distribution du contenu.
  const unitBoundaryInfo = useMemo(() => {
    const info = new Map<string, { title: string; icon: string }>();
    trailSteps.forEach((step, i) => {
      if (i > 0 && step.unitId === trailSteps[i - 1].unitId) return;
      const unit = UNIT_CONFIGS.find(u => u.id === step.unitId);
      if (unit) info.set(step.id, { title: displayUnitTitle(unit.id, t), icon: unit.icon });
    });
    return info;
  }, [trailSteps, t]);

  // Utiliser displayTrailSteps (sans quiz) pour les compteurs du header
  const totalSteps  = displayTrailSteps.length;
  const doneSteps   = displayTrailSteps.filter(s => s.status === 'done').length;
  const activeSteps = displayTrailSteps.filter(s => s.status === 'in_progress').length;

  // ── Collectibles ──────────────────────────────────────────────────────────

  const completedUnits = useMemo(() =>
    UNIT_CONFIGS
      .filter(u => {
        const steps = trailSteps.filter(s => s.unitId === u.id);
        return steps.length > 0 && steps.every(s => s.status === 'done');
      })
      .map(u => u.id),
  [trailSteps]);

  const wordsReviewedCount = useMemo(() => {
    if (!userProgress?.wordProgress) return 0;
    return Object.values(userProgress.wordProgress).filter(
      (wp: any) => wp?.nextReview != null
    ).length;
  }, [userProgress]);

  const totalXPForCollect = useMemo(
    () => parseInt(localStorage.getItem(`${languageCode}-totalXP`) || '0'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [languageCode, dailyXP], // dailyXP changes whenever XP is earned
  );

  const collectibles = useMemo(() => getCollectibles(languageCode), [languageCode]);

  const unlockedIds = useMemo(
    () => getUnlockedIds(collectibles, streakCount, totalXPForCollect, completedUnits, wordsReviewedCount),
    [collectibles, streakCount, totalXPForCollect, completedUnits, wordsReviewedCount],
  );

  // Ref pour ignorer les déblocages déjà acquis au démarrage de la session.
  // Résout le bug "changement de navigateur" : localStorage vide → toutes les
  // animations se rejouent. Le premier rendu non-vide de unlockedIds est traité
  // comme le snapshot initial (rien à animer), les suivants comme de vrais nouveaux déblocages.
  const collectInitDoneRef = useRef(false);
  const activeNodeRef      = useRef<HTMLDivElement>(null);
  // « Ibis marcheur » : des empreintes remplacent les pointillés du sentier
  const [footprintsReady, setFootprintsReady] = useState(false);
  const ibisCurrentIndex = useMemo(() => {
    const idx = displayTrailSteps.findIndex(s => s.id === continueStep?.id);
    return idx >= 0 ? idx : displayTrailSteps.length - 1;
  }, [displayTrailSteps, continueStep]);
  const trailLayoutKey = useMemo(
    () => displayTrailSteps.map(s => `${s.id}:${s.status}`).join('|'),
    [displayTrailSteps]
  );

  // Détection de nouveaux déverrouillages — une seule notification par objet
  useEffect(() => {
    if (unlockedIds.size === 0) return;

    const shown = getShownIds();

    if (!collectInitDoneRef.current) {
      // Premier rendu non-vide : on considère tous les collectibles déjà débloqués
      // comme "déjà vus" pour ne pas rejouer leurs animations.
      collectInitDoneRef.current = true;
      let changed = false;
      unlockedIds.forEach(id => {
        if (!shown.has(id)) { shown.add(id); changed = true; }
      });
      if (changed) localStorage.setItem(shownCollectiblesKey, JSON.stringify([...shown]));
      return;
    }

    // Déblocage réel pendant la session courante
    const newlyUnlocked = collectibles.find(c => unlockedIds.has(c.id) && !shown.has(c.id));
    if (!newlyUnlocked) return;
    shown.add(newlyUnlocked.id);
    localStorage.setItem(shownCollectiblesKey, JSON.stringify([...shown]));
    setCollectibleModal(newlyUnlocked);
  }, [unlockedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Célébration de fin d'unité — une seule fois par unité (mémorisé en localStorage)
  useEffect(() => {
    if (!trailSteps.length) return;
    for (const unit of UNIT_CONFIGS) {
      const unitSteps = trailSteps.filter(s => s.unitId === unit.id);
      if (!unitSteps.length) continue;
      if (!unitSteps.every(s => s.status === 'done')) continue;
      const key = `jogolinga-unit-celebrated-${languageCode}-${unit.id}`;
      if (localStorage.getItem(key)) continue;
      localStorage.setItem(key, '1');
      setCelebrationUnitId(unit.id);
      break;
    }
  }, [trailSteps, languageCode]);

  // ── CategoryPreview words (computed only when needed) ────────────────────

  const previewCategoryWords = useMemo(() => {
    if (!showPreview || !selectedCategory) return null;
    const all = Object.entries(categories[selectedCategory]).filter(
      ([key]) => !key.includes('_')
    ) as [string, WordData][];
    if (!selectedWordRange) return all;
    // selectedWordRange indexes getVocabWordKeys (the trail's chunk split),
    // not the raw entries — which also hold section headers and Kama's
    // narrative cards. Slicing the raw entries directly made "Lire les
    // hiéroglyphes · 1" open only 15 real letters out of the 20 its trail
    // node counts, so it stayed at "15/20" forever. Map the vocab range back
    // to raw indices. Between two parts, the cut falls just before the
    // first section header in the gap, so a chapter's closing cards (its
    // « Révision : … » quiz) stay in its own part and the next part opens
    // on its own title (see LESSON_BREAKS); with no header in the gap, the
    // narrative cards travel with the word that follows them. Trailing
    // cards go with the last part.
    const vocabKeys = getVocabWordKeys(selectedCategory);
    const { start, end } = selectedWordRange;
    const rawIndexOf = (vocabIdx: number) => all.findIndex(([key]) => key === vocabKeys[vocabIdx]);
    const cutBefore = (vocabIdx: number) => {
      if (vocabIdx <= 0) return 0;
      if (vocabIdx >= vocabKeys.length) return all.length;
      const prev = rawIndexOf(vocabIdx - 1);
      const next = rawIndexOf(vocabIdx);
      for (let i = prev + 1; i < next; i++) {
        if (all[i][1].isSectionHeader) return i;
      }
      return prev + 1;
    };
    return all.slice(cutBefore(start), cutBefore(end));
  }, [showPreview, selectedCategory, categories, selectedWordRange, getVocabWordKeys]);

  // ── Language display names
  const LANG_NAMES: Record<string, string> = {
    wf: 'WOLOF', ba: 'BAMBARA', la: 'LINGALA',
    ff: 'PEUL', co: 'COPTE', sw: 'SWAHILI', ka: 'KAMA', yo: 'YORUBA',
  };

  // ── "Le Sentier" translated into each target language (with determiner)
  const SENTIER_NAMES: Record<string, string> = {
    ba: 'Sira',         // Bambara  — pas d'article (langue isolante)
    wf: 'Yoon wi',      // Wolof    — article postposé classe W
    la: 'Nzela',        // Lingala  — pas d'article
    ff: 'Laawol ngol',  // Peul     — article postposé classe ol
    sw: 'Njia',         // Swahili  — pas d'article
    yo: 'Ọ̀nà',          // Yoruba   — « le chemin », pas d'article obligatoire
    co: 'ⲡⲙⲁⲓⲧ',      // Copte    — article préposé masc. ⲡ- (pe)
    ka: 'Wḏꜣt',         // Kama     — égyptien ancien, « la voie » (var. de wꜣt, apparentée à l'Œil Oudjat)
  };
  const sentierTitle = SENTIER_NAMES[languageCode] ?? 'Le Sentier';

  // ── Correspondances en langues africaines pour le nom du sentier (survol) ──
  // Clé i18n par langue de contenu plutôt que texte en dur — ce champ est de
  // la même nature que `leadSignNote`/`reading` (prose comparative savante),
  // mais comme il vit ici plutôt que dans les données de vocabulaire, il suit
  // le même mécanisme que VOCAB_UNIT_WORD_KEYS/UNIT_TEXT_KEYS ci-dessus.
  const SENTIER_TOOLTIP_KEYS: Partial<Record<string, string>> = {
    ka: 'categorySelection.sentierTooltipKama',
  };
  const sentierTooltipKey = SENTIER_TOOLTIP_KEYS[languageCode];
  const sentierTooltip = sentierTooltipKey ? t(sentierTooltipKey) : undefined;

  // ── Graphie hiéroglyphique du nom du sentier ──
  // Kama : D10-N31, rébus condensé — D10 (l'œil Oudjat) sert de phonogramme
  // pour wḏꜣt, homophone exact de wꜣt/wḏꜣ.t « route, chemin » ; N31 (route
  // bordée de buissons) porte le déterminatif de route/chemin/voyage.
  const SENTIER_GLYPHS: Partial<Record<string, string>> = {
    ka: '𓁹𓈐',
  };
  const sentierGlyphs = SENTIER_GLYPHS[languageCode];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
    {/* Overlay preview — rendu via portal dans document.body pour éviter que
        les transforms des ancêtres (Framer Motion) cassent position:fixed */}
    {showPreview && selectedCategory && previewCategoryWords && ReactDOM.createPortal(
      languageCode === 'ka' ? (
        <KamaCategoryPreview
          // Remounts for each lesson: « Leçon suivante » swaps the lesson in
          // place, and KamaLearnMode's page index/segment state must not
          // carry over from the previous one.
          key={`${selectedCategory}:${selectedWordRange?.start ?? ''}`}
          category={selectedCategory}
          words={previewCategoryWords}
          onBack={handleBackFromPreview}
          nextLesson={nextKamaLesson}
          onLessonComplete={onKamaLessonComplete}
          onAnswer={onKamaAnswer}
          isTestableWord={(key) => isKamaWordTestable?.(selectedCategory, key) ?? true}
        />
      ) : (
        <CategoryPreview
          category={selectedCategory}
          words={previewCategoryWords}
          onStartLearning={handleStartLearning}
          onStartRevision={handleStartRevision}
          onBack={handleBackFromPreview}
          completedWords={loadLearnedWordsFromStorage(selectedCategory)}
          sessionLearnedWords={sessionLearnedWords}
          onBackToMenu={onBackToMenu}
          isMobileView={isMobileView}
          languageCode={languageCode}
          userProgress={userProgress}
        />
      ),
      document.body
    )}
    {/* Sentier — toujours dans le DOM, le scroll window est préservé */}
    <div className="sentier-screen" data-theme={theme}>
      <TribalBorder />
      {onBackToMenu && (
        <FloatingBackButton onBack={onBackToMenu} position="top-left" variant="minimal" />
      )}

      {/* Backdrop ferme le popup rejouer */}
      {convReplayPopup && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 99 }}
          onClick={() => setConvReplayPopup(null)}
        />
      )}

      <AnimatePresence>
        {shieldToast && (
          <motion.div
            className="sentier-toast sentier-toast--shield"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <EmojiText text={shieldToast} />
          </motion.div>
        )}
        {lockedToast && (
          <motion.div
            className="sentier-toast"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Lock size={14} strokeWidth={1.75} style={{ verticalAlign: '-2px', marginRight: '4px' }} />{lockedToast}
          </motion.div>
        )}
        {shieldUsedModal && (
          <motion.div
            className="coll-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShieldUsedModal(null)}
          >
            <motion.div
              className="coll-card"
              initial={{ scale: 0.8, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="coll-card__emoji"><Shield size={32} strokeWidth={1.5} /></div>
              <h3 className="coll-card__name">{t('categorySelection.shieldActivated')}</h3>
              <p className="coll-card__desc">
                {t('categorySelection.streakPreservedBefore')}<strong>{shieldUsedModal.streak}</strong>{t('categorySelection.streakPreservedAfter', { count: shieldUsedModal.streak })}
              </p>
              <p className="coll-card__desc" style={{ marginTop: 6, opacity: 0.7, fontSize: '0.82rem' }}>
                {shieldUsedModal.remaining > 0
                  ? t('categorySelection.shieldsRemaining', { count: shieldUsedModal.remaining })
                  : t('categorySelection.noShieldsLeft')}
              </p>
              <button className="coll-card__btn" onClick={() => setShieldUsedModal(null)}>
                {t('categorySelection.understood')}
              </button>
            </motion.div>
          </motion.div>
        )}

      </AnimatePresence>

      {/* Shield info modal — AnimatePresence séparé pour éviter de bloquer le scroll à la fermeture */}
      <AnimatePresence>
        {shieldInfoModal && (
          <motion.div
            className="coll-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, pointerEvents: 'none' }}
            transition={{ duration: 0.18 }}
            onClick={() => setShieldInfoModal(false)}
          >
            <motion.div
              className="cauri-modal"
              initial={{ scale: 0.8, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              onClick={e => e.stopPropagation()}
            >
              {(() => {
                // Même règles que useDailyStreak : max 2 (4 dès 50 jours), +1 cauri tous les 7 jours de série
                const maxShields = streakCount >= 50 ? 4 : 2;
                const isFull = shieldCount >= maxShields;
                const daysToward = streakCount % 7;
                return (
                  <>
                    <div className="cauri-modal__slots">
                      {Array.from({ length: maxShields }, (_, i) => (
                        <span key={i} className="cauri-modal__slot">
                          <CauriIcon filled={i < shieldCount} size={maxShields > 2 ? 48 : 64} />
                        </span>
                      ))}
                    </div>
                    <div className="cauri-modal__heading">
                      <h3 className="cauri-modal__title">{t('categorySelection.shieldsInfoTitle')}</h3>
                      <span className="cauri-modal__count">
                        {shieldCount > 0
                          ? t('categorySelection.shieldsStatus', { count: shieldCount, max: maxShields })
                          : t('categorySelection.noShieldsYet')}
                      </span>
                    </div>
                    <div className="cauri-modal__rules">
                      <div className="cauri-modal__rule">
                        <span className="cauri-modal__tag">{t('categorySelection.shieldRuleOneDayTag')}</span>
                        <span>{t('categorySelection.shieldRuleOneDay')}</span>
                      </div>
                      <div className="cauri-modal__rule">
                        <span className="cauri-modal__tag cauri-modal__tag--terra">{t('categorySelection.shieldRuleMoreDaysTag')}</span>
                        <span>{t('categorySelection.shieldRuleMoreDays')}</span>
                      </div>
                    </div>
                    <div className="cauri-modal__progress">
                      <div className="cauri-modal__progress-head">
                        <span className="cauri-modal__kicker">{t('categorySelection.nextShieldLabel')}</span>
                        <span className="cauri-modal__days">{t('categorySelection.nextShieldProgress', { days: daysToward })}</span>
                      </div>
                      <div className="cauri-modal__bars">
                        {Array.from({ length: 7 }, (_, i) => (
                          <div key={i} className="cauri-modal__bar-col">
                            <span className={`cauri-modal__bar${i < daysToward ? ' cauri-modal__bar--on' : ''}`} />
                            <span className="cauri-modal__bar-label">{i + 1}</span>
                          </div>
                        ))}
                      </div>
                      <span className="cauri-modal__note">
                        {isFull
                          ? t('categorySelection.shieldsFullNote')
                          : t('categorySelection.shieldsMaxNote', { max: maxShields })}
                      </span>
                    </div>
                    <button className="cauri-modal__btn" onClick={() => setShieldInfoModal(false)}>
                      {t('categorySelection.understood')}
                    </button>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* En-tête */}
      <div className="sentier-header">
        <p className="sentier-header__route">
          {t('categorySelection.yourRoute')} · {LANG_NAMES[languageCode] ?? languageCode.toUpperCase()}
        </p>
        {sentierGlyphs && (
          <p className="sentier-header__glyphs" aria-hidden="true">{sentierGlyphs}</p>
        )}
        <h1 className="sentier-header__title">
          {sentierTooltip ? (
            <span className="sentier-title-hover" tabIndex={0}>
              {sentierTitle}
              <span className="sentier-title-tooltip" role="tooltip">{sentierTooltip}</span>
            </span>
          ) : (
            sentierTitle
          )}
        </h1>
        <p className="sentier-header__stats">
          {t('categorySelection.stepsProgress', { total: totalSteps, done: doneSteps })}
          {activeSteps > 0 && t('categorySelection.inProgressSuffix', { count: activeSteps })}
        </p>
        <div className="sentier-xp-bar">
          <motion.div
            initial={false}
            animate={{ width: `${globalPct}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
        <p className="sentier-header__xp-label">
          {t('categorySelection.wordsLearnedProgress', { learned: totalLearned + totalSentLearned, total: totalWords })}
        </p>

        {/* ── Streak widget ── */}
        <div className="sentier-streak-widget">
          {/* Ligne 1 : série + calendrier 7 jours */}
          <div className="sentier-streak-row">
            <div className="sentier-streak-left">
              <span className="sentier-streak-fire"><Flame size={16} strokeWidth={1.75} fill="currentColor" /></span>
              {streakReady
                ? <><span className="sentier-streak-count">{streakCount}</span>
                    <span className="sentier-streak-unit">{t('categorySelection.day', { count: streakCount })}</span></>
                : <span className="sentier-streak-count" style={{ opacity: 0.3 }}>—</span>
              }
              <button
                className="sentier-streak-shields"
                title={t('categorySelection.shieldsInfoTitle')}
                onClick={() => setShieldInfoModal(true)}
              >
                {Array.from({ length: streakCount >= 50 || shieldCount > 2 ? 4 : 2 }, (_, i) => (
                  <span key={i} className={`sentier-streak-shield-slot${i < shieldCount ? ' sentier-streak-shield-slot--active' : ''}`}><Shield size={13} strokeWidth={1.75} /></span>
                ))}
              </button>
            </div>
            <div className="sentier-streak-calendar">
              {(() => {
                const _hist = JSON.parse(localStorage.getItem('jogolinga-activity-history') || '[]') as string[];
                return getStreakCalendar(_hist, _hist.includes(SESSION_DATE) || dailyXP >= dailyGoal, streakCount).map((d, i) => (
                <div key={i} className="sentier-streak-col">
                  <div
                    className={[
                      'sentier-streak-day',
                      d.active   ? 'sentier-streak-day--active'  : '',
                      d.isToday  ? 'sentier-streak-day--today'   : '',
                      d.isFuture ? 'sentier-streak-day--future'  : '',
                    ].join(' ').trim()}
                  />
                  <span className={`sentier-streak-daylabel${d.isToday ? ' sentier-streak-daylabel--today' : ''}`}>
                    {d.label}
                  </span>
                </div>
              ));
              })()}
            </div>
          </div>

          {/* Ligne 2 : objectif XP journalier */}
          <div className="sentier-daily-goal" onClick={() => setShowDailyModal(true)} style={{ cursor: 'pointer' }}>
            <div className="sentier-daily-goal__header">
              <span className="sentier-daily-goal__icon"><Zap size={14} strokeWidth={1.75} /></span>
              <span className="sentier-daily-goal__title">{t('categorySelection.dailyGoalTitle')}</span>
              <span className={`sentier-daily-goal__xp${dailyXP >= dailyGoal ? ' sentier-daily-goal__xp--done' : ''}`}>
                {Math.min(dailyXP, dailyGoal)}&thinsp;/&thinsp;{dailyGoal} XP
              </span>
            </div>
            <div className="sentier-daily-goal__track">
              <motion.div
                className={`sentier-daily-goal__fill${dailyXP >= dailyGoal ? ' sentier-daily-goal__fill--done' : ''}`}
                initial={false}
                animate={{ width: `${Math.min(100, (dailyXP / dailyGoal) * 100)}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
            {dailyXP >= dailyGoal && (
              <p className="sentier-daily-goal__success">{t('categorySelection.goalReachedMsg')}</p>
            )}
          </div>
        </div>

      </div>

      {/* Arches décoratives */}
      <div className="sentier-arches" aria-hidden="true" />

      {/* Trail */}
      <div
        className={`sentier-trail sentier-trail--footprints${footprintsReady ? ' sentier-trail--footprints-ready' : ''}`}
      >
        <IbisTrailWalker
          currentIndex={ibisCurrentIndex}
          layoutKey={trailLayoutKey}
          onReady={setFootprintsReady}
        />
        {displayTrailSteps.map((step, i) => {
          const isLeft            = i % 2 === 0;
          const isLast            = i === displayTrailSteps.length - 1;
          const isContinue        = step.id === continueStep?.id;
          const isNext            = step.id === nextStep?.id;
          const milestone         = unitBoundaryInfo.get(step.id);
          const isBeforeMilestone = !isLast && unitBoundaryInfo.has(displayTrailSteps[i + 1]?.id ?? '');
          const hostedQuiz        = quizBesideStep.get(step.id);

          let nodeClass = 'sentier-node';
          let nodeIcon: React.ReactNode = null;
          if (step.status === 'done') {
            nodeClass += ' sentier-node--done';
            nodeIcon = <Check size={20} strokeWidth={2.5} />;
          } else if (isContinue) {
            nodeClass += ' sentier-node--active';
            nodeIcon = <span className="sentier-play-icon">▶</span>;
          } else if (step.status === 'in_progress') {
            nodeClass += ' sentier-node--progress';
            nodeIcon = <BogolonIcon variant={getBogolonVariant(step.id)} size={18} />;
          } else if (isNext) {
            nodeClass += ' sentier-node--next';
            nodeIcon = <BogolonIcon variant={getBogolonVariant(step.id)} size={18} />;
          } else if (step.status === 'available') {
            nodeClass += ' sentier-node--available';
            nodeIcon = <BogolonIcon variant={getBogolonVariant(step.id)} size={18} />;
          } else {
            nodeClass += ' sentier-node--locked';
            nodeIcon = <Lock size={16} strokeWidth={1.75} />;
          }
          if (step.lockedByGuide) nodeClass += ' sentier-node--guide-locked';

          const isConvStep = step.id.startsWith('conversation-');
          const unitLabel = isConvStep
            ? t('categorySelection.stepUnitSteps')
            : step.id.startsWith('vocab-')
              ? displayVocabUnitWord(getVocabUnitWord(step.id), t)
              : t('categorySelection.stepUnitWords');
          const subLabel = step.status === 'locked'
            ? `${step.total} ${unitLabel}`
            : step.status === 'done'
              ? t('categorySelection.completed')
              : isContinue && step.learned === 0
                ? `${step.total} ${unitLabel} · ${t('categorySelection.startArrow')}`
                : step.learned > 0
                  ? `${step.learned}/${step.total}${step.status === 'in_progress' ? t('categorySelection.resumeSuffix') : ''}`
                  : `${step.total} ${unitLabel}`;

          // Progress ring (Duolingo-style)
          const ringProgress = step.status === 'done'
            ? 1
            : step.total > 0 ? Math.min(step.learned / step.total, 1) : 0;
          const ringR    = 29;
          const ringCirc = 2 * Math.PI * ringR;
          const ringDash = ringCirc * (1 - ringProgress);

          const nextDisplayStep = displayTrailSteps[i + 1];
          const isLastVocabOfUnit =
            (lm: TrailLandmark) => lm.atEndOfUnit === step.unitId &&
            (step.id.startsWith('vocab-') || step.id.startsWith('conversation-')) &&
            (!nextDisplayStep ||
             nextDisplayStep.unitId !== step.unitId ||
             nextDisplayStep.id.startsWith('grammar-') ||
             nextDisplayStep.id.startsWith('phrases-'));
          const landmark = trailLandmarks.find(lm =>
            (lm.atStepIndex !== undefined && lm.atStepIndex === i) ||
            (lm.atEndOfUnit !== undefined && isLastVocabOfUnit(lm))
          );

          return (
            <React.Fragment key={step.id}>
              {milestone && (() => {
                const unitCfg = UNIT_CONFIGS.find(u => u.id === step.unitId);
                const unitCats = unitCfg ? unitCategories(unitCfg.categories) : [];
                const unitTotalWords = unitCats.reduce(
                  (sum, cat) => sum + (categoryStats[cat]?.total ?? 0), 0
                );
                return (
                  <>
                    <div className="sentier-milestone">
                      <div className="sentier-milestone__line" />
                      <div className="sentier-milestone__content">
                        <span className="accent-diamond" aria-hidden="true" />
                        <span className="sentier-milestone__label">{milestone.title}</span>
                        <span className="accent-diamond" aria-hidden="true" />
                      </div>
                      <div className="sentier-milestone__line sentier-milestone__line--right" />
                    </div>
                    {unitCats.length > 0 && (
                      <div className="sentier-milestone__detail">
                        <p className="sentier-milestone__cats">
                          {unitCats.map(c => displayCategoryLabel(c, t)).join(' · ')}
                        </p>
                        <p className="sentier-milestone__summary">
                          {t('categorySelection.unitSummary', {
                            count: unitCats.length,
                            categoryWord: t('categorySelection.category', { count: unitCats.length }),
                            words: unitTotalWords,
                          })}
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}
              <div
                ref={isContinue ? activeNodeRef : undefined}
                data-trail-idx={i}
                data-has-conn={!isLast && !isBeforeMilestone ? '1' : '0'}
                className={`sentier-step ${isLeft ? 'sentier-step--left' : 'sentier-step--right'}${milestone ? ' sentier-step--after-milestone' : ''}`}
              >
                {!isLast && !isBeforeMilestone && (
                  <svg
                    className="sentier-conn-svg"
                    width="100%"
                    height="56"
                    viewBox="0 0 300 56"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path
                      d={isLeft
                        ? "M 0 0 L 0 18 Q 0 56 38 56 L 300 56"
                        : "M 300 0 L 300 18 Q 300 56 262 56 L 0 56"
                      }
                      fill="none"
                      stroke="rgba(200, 112, 64, 0.45)"
                      strokeWidth="2"
                      strokeDasharray="10 7"
                      strokeLinecap="round"
                      className="sentier-dash-path"
                    />
                  </svg>
                )}

                <div className="sentier-node-wrap">
                  {step.status !== 'locked' && (
                    <svg className="sentier-ring-svg" width="68" height="68" viewBox="0 0 68 68" aria-hidden="true">
                      <circle cx="34" cy="34" r={ringR} fill="none" stroke="rgba(200,112,64,0.13)" strokeWidth="3.5" />
                      {ringProgress > 0 && (
                        <circle
                          cx="34" cy="34" r={ringR}
                          fill="none"
                          stroke="var(--color-accent)"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeDasharray={ringCirc}
                          strokeDashoffset={ringDash}
                          transform="rotate(-90 34 34)"
                        />
                      )}
                    </svg>
                  )}
                  <motion.button
                    className={nodeClass}
                    onClick={() => { step.onClick(); window.scrollTo({ top: 0, behavior: 'auto' }); }}
                    disabled={step.status === 'locked' && !step.lockedByPremium && !step.lockedByGuide}
                    whileHover={step.status !== 'locked' || step.lockedByPremium || step.lockedByGuide ? { scale: 1.06 } : {}}
                    whileTap={step.status !== 'locked' || step.lockedByPremium || step.lockedByGuide ? { scale: 0.93 } : {}}
                  >
                    {nodeIcon}
                  </motion.button>
                </div>

                <div className="sentier-label">
                  <p className="sentier-label__name">{step.label}</p>
                  <p className="sentier-label__sub">{subLabel}</p>
                  {step.status !== 'done' && step.total > 0 && (
                    <p className="sentier-label__time">
                      {estimateStepDuration(step.id, step.total)}
                    </p>
                  )}
                </div>

                {/* Popup rejouer — conversation terminée */}
                {isConvStep && convReplayPopup === step.id.replace('conversation-', '') && (
                  <div className="conv-replay-popup">
                    <p className="conv-replay-popup__score">{t('categorySelection.replayScore', { learned: step.learned, total: step.total })}</p>
                    <button
                      className="conv-replay-popup__btn"
                      onClick={() => {
                        setConvReplayPopup(null);
                        onSelectConversation!(step.id.replace('conversation-', ''));
                      }}
                    >
                      <EmojiText text={t('categorySelection.replay') as string} />
                    </button>
                  </div>
                )}

                {/* Landmarks latéraux (Arène, Scène) */}
                {landmark && (
                  <button
                    className="sentier-landmark-side"
                    onClick={() => onOpenMiniGame?.(landmark.game)}
                    title={landmark.sublabel}
                  >
                    <span className="sentier-landmark__emoji">
                      {landmark.game === 'music' ? <Music size={20} strokeWidth={1.75} /> : <Swords size={20} strokeWidth={1.75} />}
                    </span>
                    <span className="sentier-landmark__label">{landmark.label}</span>
                    <span className="sentier-landmark__sub">{landmark.sublabel}</span>
                    {landmark.game === 'challenge' && pendingChallengesCount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '-4px',
                        background: 'linear-gradient(135deg, var(--color-error), var(--color-error-dark))',
                        minWidth: '18px',
                        height: '18px',
                        borderRadius: '9px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 700,
                        padding: '0 4px',
                        boxShadow: '0 0 6px rgba(239,68,68,0.5)',
                        lineHeight: 1,
                      }}>
                        {pendingChallengesCount > 9 ? '9+' : pendingChallengesCount}
                      </span>
                    )}
                  </button>
                )}

                {/* Carte quiz latérale (Histoire, Géographie, Politique…) */}
                {hostedQuiz && (() => {
                  const qData = QUIZ_THEMES_ON_TRAIL.find(q => `quiz-${q.theme}` === hostedQuiz.id);
                  const QIcon = (qData && QUIZ_THEME_ICONS[qData.theme]) || HelpCircle;
                  const qSub = hostedQuiz.learned > 0
                    ? `${hostedQuiz.learned}/${hostedQuiz.total}`
                    : (qData ? displayQuizThemeSublabel(qData.theme, t) : t('categorySelection.questionsCount', { count: hostedQuiz.total }));
                  return (
                    <button
                      className={`sentier-quiz-side${hostedQuiz.status === 'locked' ? ' sentier-quiz-side--locked' : ''}${hostedQuiz.status === 'done' ? ' sentier-quiz-side--done' : ''}`}
                      onClick={() => { window.scrollTo({ top: 0, behavior: 'auto' }); hostedQuiz.onClick(); }}
                      disabled={hostedQuiz.status === 'locked'}
                      title={hostedQuiz.label}
                    >
                      <span className="sentier-quiz-card__emoji">{hostedQuiz.status === 'locked' ? <Lock size={18} strokeWidth={1.75} /> : <QIcon size={18} strokeWidth={1.75} />}</span>
                      <span className="sentier-quiz-card__label">{hostedQuiz.label.toUpperCase()}</span>
                      <span className="sentier-quiz-card__sub">{qSub}</span>
                    </button>
                  );
                })()}

              </div>
            </React.Fragment>
          );
        })}
      </div>

    </div>

    {showDailyModal && (
      <DailyGoalModal
        dailyXP={dailyXP}
        goal={dailyGoal}
        streak={streakReady ? streakCount : 0}
        shields={shieldCount}
        onGoalChange={setDailyGoal}
        onClose={() => setShowDailyModal(false)}
      />
    )}

    <AnimatePresence>
      {collectibleModal && (
        <CollectibleModal
          key={collectibleModal.id}
          item={collectibleModal}
          onClose={() => setCollectibleModal(null)}
        />
      )}
      {!collectibleModal && celebrationUnitId !== null && (() => {
        const unit = UNIT_CONFIGS.find(u => u.id === celebrationUnitId);
        return unit ? (
          <UnitCelebration
            key={celebrationUnitId}
            unit={unit}
            onClose={() => setCelebrationUnitId(null)}
          />
        ) : null;
      })()}
    </AnimatePresence>
    </>
  );
};

export default CategorySelection;
